import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { apiJson } from "../lib/queryClient";
import type { Entitlement } from "../lib/subscription";

type ConfirmResponse = {
  paid: boolean;
  entitlement: Entitlement;
};

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const sessionId = params.get("session_id");
  const ranRef = useRef(false);

  const [state, setState] = useState<"pending" | "active" | "processing" | "error">(
    "pending",
  );
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    let cancelled = false;

    async function confirm() {
      try {
        if (sessionId) {
          // Abo direkt spiegeln (wartet nicht auf den Webhook).
          for (let attempt = 0; attempt < 5 && !cancelled; attempt++) {
            const res = await apiJson<ConfirmResponse>(
              "POST",
              "/api/subscription/checkout/confirm",
              { sessionId },
            );
            if (res.entitlement?.active) {
              await qc.invalidateQueries({ queryKey: ["/api/subscription/me"] });
              if (!cancelled) setState("active");
              return;
            }
            await new Promise((r) => setTimeout(r, 1500));
          }
        }
        // Fallback: Status frisch laden (z.B. ohne session_id).
        await qc.invalidateQueries({ queryKey: ["/api/subscription/me"] });
        if (!cancelled) setState("processing");
      } catch (err) {
        if (!cancelled) {
          setState("error");
          setMessage((err as Error).message || "Bestätigung fehlgeschlagen.");
        }
      }
    }

    void confirm();
    return () => {
      cancelled = true;
    };
  }, [sessionId, qc]);

  return (
    <div className="auth-shell">
      <div className="auth-frame">
        <div className="px-6 pt-16 pb-6 text-center flex-1 flex flex-col items-center justify-center">
          {state === "active" ? (
            <>
              <CheckCircle2 size={56} className="text-[var(--color-go,#16a34a)]" />
              <h1 className="text-[26px] font-bold tracking-tight mt-5 text-[var(--color-app-text)]">
                Freigeschaltet
              </h1>
              <p className="text-[14px] text-[var(--color-app-text-secondary)] mt-1">
                Vielen Dank! Dein Abo ist aktiv und die App ist freigeschaltet.
              </p>
              <button
                className="btn-primary w-full mt-8"
                onClick={() => navigate("/", { replace: true })}
                data-testid="button-continue"
              >
                Zur App
              </button>
            </>
          ) : state === "error" ? (
            <>
              <h1 className="text-[24px] font-bold tracking-tight text-[var(--color-app-text)]">
                Etwas ist schiefgelaufen
              </h1>
              <p className="text-[14px] text-[var(--color-app-text-secondary)] mt-2">
                {message ?? "Die Zahlung konnte nicht bestätigt werden."}
              </p>
              <Link
                to="/abo"
                className="btn-primary w-full mt-8"
                data-testid="link-subscription"
              >
                Abo-Status ansehen
              </Link>
            </>
          ) : state === "processing" ? (
            <>
              <h1 className="text-[24px] font-bold tracking-tight text-[var(--color-app-text)]">
                Zahlung wird verarbeitet
              </h1>
              <p className="text-[14px] text-[var(--color-app-text-secondary)] mt-2">
                Deine Zahlung wird noch verarbeitet. Das kann einen Moment dauern.
              </p>
              <Link
                to="/abo"
                className="btn-primary w-full mt-8"
                data-testid="link-subscription"
              >
                Abo-Status ansehen
              </Link>
            </>
          ) : (
            <>
              <div className="brand-tile" aria-hidden="true" />
              <h1 className="text-[22px] font-bold tracking-tight mt-5 text-[var(--color-app-text)]">
                Zahlung wird bestätigt …
              </h1>
              <p className="text-[14px] text-[var(--color-app-text-secondary)] mt-1">
                Einen Augenblick bitte.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
